/* eslint-disable camelcase */
import { ExportFormat } from '../../constants/file';
import { decamelizeKeys } from '../../lib';
import { getBackVersion } from '../../config';
import { NewExternalAccountSnapshot } from '../../models/Banking';
import {
    LookupRentalLandlord,
    LandlordExternalAccountInput,
    RentalLandlordInput,
    RentalLandlordUpsertParams,
} from '../../models/RentalPayment';
import { ContractRentalSplitCountInput } from '../../models/RentalPayment/SplitCount';
import api, { rawClient } from '../api';

const searchLandlordByDocument = (
    document: string
): Promise<LookupRentalLandlord> =>
    api.request<LookupRentalLandlord>({
        method: 'GET',
        action: 'RENTAL_LANDLORD_AUTOFILL',
        url: `${getBackVersion()}/agent/rental/landlord/autofill/${document}`,
    });

const downloadLandlordsReport = (format: ExportFormat): Promise<void> =>
    rawClient.download({
        url: `${getBackVersion()}/agent/rental/landlord/${format}`,
    });

const getLandlord = (id: number): Promise<RentalLandlordInput> =>
    api.request<RentalLandlordInput>({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/landlord/${id}`,
    });

const getLandlords = (): Promise<RentalLandlordInput[]> =>
    api.request<RentalLandlordInput[]>({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/landlord`,
    });

const getScheduledCountsByDestinationId = (
    id: number
): Promise<ContractRentalSplitCountInput[]> =>
    api.request<ContractRentalSplitCountInput[]>({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/landlord/scheduled_count/${id}`,
    });

const upsertLandlord = (
    landlord: RentalLandlordUpsertParams
): Promise<RentalLandlordInput> =>
    api.request<RentalLandlordInput>({
        action: 'RENTAL_LANDLORD_CREATION',
        method: 'POST',
        url: `${getBackVersion()}/agent/rental/landlord`,
        data: decamelizeKeys(landlord),
    });

const removeLandlord = (landlordId: number): Promise<void> =>
    api.request({
        method: 'DELETE',
        url: `${getBackVersion()}/agent/rental/landlord/${landlordId}`,
        params: { action: 'remove' },
    });

const archiveLandlord = (
    landlordId: number,
    action: 'archive' | 'unarchive'
): Promise<RentalLandlordInput> =>
    api.request<RentalLandlordInput>({
        method: 'DELETE',
        url: `${getBackVersion()}/agent/rental/landlord/${landlordId}`,
        params: { action: action },
    });

const createLandlordExternalAccount = (
    landlordId: number,
    externalAccount: NewExternalAccountSnapshot
): Promise<LandlordExternalAccountInput> =>
    api.request<LandlordExternalAccountInput>({
        action: 'BANKING_EXTERNAL_ACCOUNT_CREATION',
        method: 'POST',
        url: `${getBackVersion()}/agent/rental/landlord/${landlordId}/external_accounts`,
        data: decamelizeKeys(externalAccount),
    });

const selectMainExternalAccount = (
    landlordId: number,
    externalAccountId: string
): Promise<RentalLandlordInput> =>
    api.request<RentalLandlordInput>({
        method: 'POST',
        url:
            `${getBackVersion()}/agent/rental/landlord/${landlordId}/external_accounts/` +
            `${externalAccountId}/main`,
    });

const removeExternalAccount = (
    landlordId: number,
    externalAccountId: string
): Promise<void> =>
    api.request<void>({
        method: 'DELETE',
        url:
            `${getBackVersion()}/agent/rental/landlord/${landlordId}/external_accounts/` +
            externalAccountId,
    });

const activateExternalAccount = (
    landlordId: number,
    externalAccountId: string
): Promise<RentalLandlordInput> =>
    api.request<RentalLandlordInput>({
        method: 'POST',
        url: `${getBackVersion()}/agent/rental/landlord/${landlordId}/external_accounts/${externalAccountId}/activate`,
    });

const deactivateExternalAccount = (
    landlordId: number,
    externalAccountId: string,
    replacementAccountId: string | undefined
): Promise<RentalLandlordInput> =>
    api.request<RentalLandlordInput>({
        method: 'POST',
        url: `${getBackVersion()}/agent/rental/landlord/${landlordId}/external_accounts/${externalAccountId}/deactivate`,
        data: {
            replacement_account_id: replacementAccountId,
        },
    });

export {
    archiveLandlord,
    createLandlordExternalAccount,
    downloadLandlordsReport,
    getLandlord,
    getLandlords,
    getScheduledCountsByDestinationId,
    removeExternalAccount,
    removeLandlord,
    searchLandlordByDocument,
    selectMainExternalAccount,
    activateExternalAccount,
    deactivateExternalAccount,
    upsertLandlord,
};
